import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import type {
  PhoneNumber,
  PhoneNumberDirection,
} from "@chat-yai/shared/admin-types";
import { phoneNumbersApi } from "@/api/endpoints/phoneNumbers";
import { integrationsApi } from "@/api/endpoints/integrations";
import { chatbotsApi } from "@/api/endpoints/chatbots";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { EmptyState } from "@/components/EmptyState";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { useSession } from "@/contexts/SessionContext";
import { canManageContent } from "@/lib/rbac";

const PHONE_RE = /^\+[0-9\-\s]{6,20}$/;

interface AddFormValues {
  phone_number: string;
  chatbot_id: string;
  direction: PhoneNumberDirection;
}

export function PhoneNumbersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { me } = useSession();
  const role =
    me?.memberships.find((m) => m.tenant_id === me.current_tenant_id)?.role ??
    "viewer";
  const canEdit = canManageContent(role);

  const [addOpen, setAddOpen] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState<PhoneNumber | null>(null);

  const twilioQuery = useQuery({
    queryKey: ["integrations", "twilio"],
    queryFn: () => integrationsApi.getTwilio(),
  });

  const listQuery = useQuery({
    queryKey: ["phone-numbers"],
    queryFn: () => phoneNumbersApi.list(),
  });

  const chatbotsQuery = useQuery({
    queryKey: ["chatbots", "all"],
    queryFn: () => chatbotsApi.list(),
  });

  const form = useForm<AddFormValues>({
    defaultValues: {
      phone_number: "",
      chatbot_id: "",
      direction: "inbound",
    },
  });

  const addMutation = useMutation({
    mutationFn: (v: AddFormValues) =>
      phoneNumbersApi.assign({
        phone_number: v.phone_number.trim(),
        chatbot_id: v.chatbot_id,
        direction: v.direction,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phone-numbers"] });
      queryClient.invalidateQueries({ queryKey: ["integrations", "twilio"] });
      toast.push("success", "Phone number assigned.");
      setAddOpen(false);
      form.reset({ phone_number: "", chatbot_id: "", direction: "inbound" });
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  const releaseMutation = useMutation({
    mutationFn: (id: string) => phoneNumbersApi.release(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["phone-numbers"] });
      queryClient.invalidateQueries({ queryKey: ["integrations", "twilio"] });
      toast.push("success", translateError("PHONE_NUMBER_RELEASED"));
      setConfirmRelease(null);
    },
    onError: (err) => {
      toast.push(
        "error",
        translateError(err instanceof ApiError ? err.code : undefined),
      );
    },
  });

  const items = listQuery.data?.items ?? [];
  const chatbots = chatbotsQuery.data?.chatbots ?? [];
  const twilioConfigured = !!twilioQuery.data?.configured;

  const openAdd = (): void => {
    form.reset({
      phone_number: "",
      chatbot_id: chatbots[0]?.id ?? "",
      direction: "inbound",
    });
    setAddOpen(true);
  };

  return (
    <div className="page" data-testid="phone-numbers-page">
      <div className="page-head">
        <div>
          <div className="page-title">Phone numbers</div>
          <div className="page-sub">
            Attach Twilio phone numbers to specific chatbots so inbound calls
            land on the right assistant.
          </div>
        </div>
        {canEdit && twilioConfigured ? (
          <div className="page-actions">
            <button
              className="btn btn-accent btn-sm"
              onClick={openAdd}
              data-testid="open-add-phone"
              disabled={chatbots.length === 0}
              title={
                chatbots.length === 0
                  ? "Create a chatbot first to attach a phone number."
                  : ""
              }
            >
              <Icon name="plus" size={12} /> Add phone number
            </button>
          </div>
        ) : null}
      </div>

      {twilioQuery.isLoading || listQuery.isLoading ? (
        <div className="empty">Loading…</div>
      ) : !twilioConfigured ? (
        <EmptyState
          icon="plug"
          title="Configure Twilio first"
          description="Phone numbers need a Twilio account before they can be attached. Set one up in the Twilio integration page."
          primary={{
            label: "Open Twilio integration",
            href: "/integrations/twilio",
            testId: "phone-numbers-empty-cta",
          }}
          testId="phone-numbers-empty-twilio"
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon="plug"
          title="No phone numbers attached"
          description="Add a Twilio phone number and connect it to a chatbot to start receiving calls."
          primary={
            canEdit && chatbots.length > 0
              ? {
                  label: "Add phone number",
                  onClick: openAdd,
                  testId: "phone-numbers-empty-add",
                }
              : undefined
          }
          testId="phone-numbers-empty"
        />
      ) : (
        <div className="card">
          <div
            className="table-row-head"
            style={{ gridTemplateColumns: "1.4fr 1fr 1.4fr 1fr 1.2fr 120px" }}
          >
            <div>Phone number</div>
            <div>Direction</div>
            <div>Chatbot</div>
            <div>Status</div>
            <div>Assigned</div>
            <div></div>
          </div>
          {items.map((p) => (
            <div
              key={p.id}
              className="list-row"
              style={{
                gridTemplateColumns: "1.4fr 1fr 1.4fr 1fr 1.2fr 120px",
              }}
              data-testid={`phone-row-${p.id}`}
            >
              <div className="mono" style={{ fontSize: 13, fontWeight: 500 }}>
                {p.phone_number}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                {p.direction}
              </div>
              <div style={{ fontSize: 13 }}>{p.chatbot_name ?? "—"}</div>
              <div>
                <span
                  className={
                    "chip " +
                    (p.status === "active" ? "chip-success" : "chip-warning")
                  }
                >
                  {p.status}
                </span>
              </div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {p.assigned_at
                  ? new Date(p.assigned_at).toLocaleDateString()
                  : "—"}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                {canEdit ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => setConfirmRelease(p)}
                    data-testid={`release-phone-${p.id}`}
                  >
                    Release
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        title="Add phone number"
        open={addOpen}
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={addMutation.isPending}
              onClick={form.handleSubmit((v) => {
                if (!PHONE_RE.test(v.phone_number.trim())) {
                  toast.push("error", translateError("PHONE_NUMBER_INVALID"));
                  return;
                }
                addMutation.mutate(v);
              })}
              data-testid="submit-phone"
            >
              {addMutation.isPending ? "Saving…" : "Attach number"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Phone number (E.164)</label>
          <input
            className="field-input mono"
            placeholder="+64-9-555-0000"
            data-testid="phone-number-input"
            {...form.register("phone_number", {
              required: true,
              pattern: PHONE_RE,
            })}
          />
          <FormError
            message={
              form.formState.errors.phone_number
                ? "Use E.164 format like +64-9-555-0000."
                : undefined
            }
          />
        </div>
        <div className="field-group">
          <label>Chatbot</label>
          <select
            className="field-input"
            data-testid="phone-chatbot-select"
            {...form.register("chatbot_id", { required: true })}
          >
            {chatbots.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field-group">
          <label>Direction</label>
          <select
            className="field-input"
            data-testid="phone-direction-select"
            {...form.register("direction")}
          >
            <option value="inbound">Inbound only</option>
            <option value="outbound">Outbound only</option>
            <option value="both">Both</option>
          </select>
        </div>
      </Modal>

      <Modal
        title="Release phone number"
        open={!!confirmRelease}
        onClose={() => setConfirmRelease(null)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setConfirmRelease(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={releaseMutation.isPending}
              onClick={() =>
                confirmRelease && releaseMutation.mutate(confirmRelease.id)
              }
              data-testid="confirm-release-phone"
            >
              {releaseMutation.isPending ? "Releasing…" : "Release number"}
            </button>
          </>
        }
      >
        {confirmRelease ? (
          <p>
            Inbound calls to <strong>{confirmRelease.phone_number}</strong> will
            stop being routed to{" "}
            <strong>{confirmRelease.chatbot_name ?? "the assigned chatbot"}</strong>.
            The number remains in your Twilio account.
          </p>
        ) : null}
      </Modal>
    </div>
  );
}
